import { useStatsigClient } from '@statsig/react-bindings'
import AccountSelector from 'components/Dashboard/AccountSelector'
import { useAuth } from 'hooks/useAuth'
import { useEffect, useMemo, useState } from 'react'
import { useHover } from 'react-aria'
import { Collection, Tree, TreeItem, TreeItemContent } from 'react-aria-components'
import { useLocation } from 'react-router-dom'
import { useFetch } from 'utils/fetch'
import { cn } from '@/lib/utils'
import RainbowLogo from '../../assets/gifs/rainbow-logo.gif'
import { dashboard } from '../../utils/data/menus/sideMenuItems'
import blandlogo from './../../assets/brand/bland-b.svg'
import blandfont from './../../assets/brand/new-bland-logo.svg'

// Custom TreeItemContent component that receives isExpanded as a prop
function SideMenuTreeItemContent({ item, isExpanded }) {
  return (
    <TreeItemContent>
      <span className="size-4 shrink-0 text-gray-600">
        {item.icon}
      </span>
      <p className={cn(
        'text-sm font-medium text-nowrap transition-opacity duration-200',
        isExpanded ? 'block' : 'hidden',
      )}
      >
        {item.title}
      </p>
    </TreeItemContent>
  )
}

export default function SideMenu({ onExpandChange: _onExpandChange }) {
  const { permissions } = useAuth()
  const { hoverProps, isHovered } = useHover({
    onHoverStart: () => {
      console.warn('hover start')
    },
    onHoverEnd: () => {
      console.warn('hover end')
    },
  })
  const [isDancing, setIsDancing] = useState(false)
  const { client: statsigClient } = useStatsigClient()
  const [accountSelectorOpen, setAccountSelectorOpen] = useState(false)
  const { data: events, isLoading: isLoadingEvents } = useFetch('api/events', {
    queryKey: ['events'],
    dataKey: ['events'],
  })
  const location = useLocation()
  const activePath = location.pathname

  const hidePathways = statsigClient.checkGate('hide_pathway_data')

  useEffect(() => {
    const handleResize = () => {
      if (window.innerHeight < 519) {
        setAccountSelectorOpen(false)
      }
    }

    window.addEventListener('resize', handleResize)
    handleResize()

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const allowedSideBarItems = useMemo(() => {
    let baseMenu = dashboard
    if (permissions?.includes('viewer')) {
      baseMenu = baseMenu.filter(page => page.query !== 'send-call')
    }
    else if (!permissions?.includes('owner') && !permissions?.includes('admin')) {
      baseMenu = baseMenu.filter(page => page.query !== 'pay')
    }

    if (!events?.length) {
      baseMenu = baseMenu.filter(page => page.query !== 'all-events')
    }

    if (hidePathways) {
      baseMenu = baseMenu.filter(page => page.query !== 'convo-pathways')
    }

    return baseMenu
  }, [permissions, isLoadingEvents, hidePathways])

  const isExpanded = isHovered || accountSelectorOpen

  return (
    <>
      {/* Backdrop for account selector */}
      {accountSelectorOpen && (
        <div
          className={`
            fixed inset-0 z-[4999] bg-black/10 transition-opacity duration-300
          `}
          onClick={() => setAccountSelectorOpen(false)}
        />
      )}

      <aside
        className={cn(
          `h-full bg-light-secondary`,
          'flex flex-col transition-all duration-200 ease-out',
          isExpanded ? 'w-60' : 'w-14',
        )}
        role="navigation"
        aria-label="Main navigation"
        {...hoverProps}
      >
        {/* Header with Logo */}

        <img
          src={isDancing ? RainbowLogo : isExpanded ? blandfont : blandlogo}
          alt="Bland"
          className={cn(
            `absolute`,
            isDancing ? 'top-3.5 left-4 h-[30px]' : 'top-4 left-4.5 h-[26px]',
          )}
          onMouseEnter={() => setIsDancing(true)}
          onMouseLeave={() => setIsDancing(false)}
        />

        <Tree
          aria-label="Side Menu Items"
          className="mt-16 flex flex-1 flex-col gap-2 px-3"
        >
          {allowedSideBarItems.map(item => (
            <TreeItem
              key={item.query || item.title}
              textValue={item.title}
              href={item.href}
              target={item.target}
              isSelected={activePath === item.href}
              className={`
                inline-flex h-8 cursor-pointer items-center justify-start gap-3
                rounded-sm px-2 py-1.5
                hover:bg-hover-light-4
                ${activePath === item.href ? 'bg-menu-select' : ''}
              `}
            >
              <SideMenuTreeItemContent item={item} isExpanded={isExpanded} />
              {item.children && (
                <Collection items={item.children}>
                  {childItem => (
                    <TreeItem
                      key={childItem.query || childItem.title}
                      textValue={childItem.title}
                      href={childItem.href}
                      target={childItem.target}
                      className="flex shrink-0 cursor-pointer p-4"
                    >
                      <SideMenuTreeItemContent item={childItem} isExpanded={isExpanded} />
                    </TreeItem>
                  )}
                </Collection>
              )}
            </TreeItem>
          ))}
        </Tree>

        {/* Footer with Account Selector */}
        <footer
          className="mb-1 h-[54px] overflow-hidden p-3"
          role="contentinfo"
          aria-label="Account management"
        >
          <AccountSelector
            open={accountSelectorOpen}
            onClick={() => setAccountSelectorOpen(true)}
            onClose={() => {
              setAccountSelectorOpen(false)
            }}
          />
        </footer>
      </aside>
    </>
  )
}
